import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { AddCircleOutline, CheckCircle, InfoOutlined, Visibility, VisibilityOff } from "@mui/icons-material";
import {
  useGetAdminChatBotProfileQuery,
  useCreateAdminChatBotProfileMutation,
  useUpdateAdminChatBotProfileMutation,
} from "features/adminUsers/adminUsers";

const API_KEY_EMAIL = "konrad@hivemechanics.io";
const API_KEY_HINT = (
  <>
    To get a chat ID and API key for this user, send an email to{" "}
    <Link href={`mailto:${API_KEY_EMAIL}`} color="inherit" sx={{ fontWeight: 600 }}>
      {API_KEY_EMAIL}
    </Link>{" "}
    and then enter them below.
  </>
);

interface Props {
  userId: number;
  canEdit?: boolean;
  isCalibrateManager?: boolean;
  chatbotDraft?: { chat_id: string; api_key: string } | null;
  onChatbotDraftChange?: (
    data: { chat_id: string; api_key: string },
    serverProfile: { chat_id?: string; api_key?: string } | null | undefined,
  ) => void;
  onSuccess?: (message: string, severity: "success" | "error") => void;
  onSaveComplete?: () => void;
}

export default function UserManagementChatBotTab({
  userId,
  canEdit = true,
  isCalibrateManager = false,
  chatbotDraft,
  onChatbotDraftChange,
  onSuccess,
  onSaveComplete,
}: Props) {
  const { data: profile, isLoading, isError, error } = useGetAdminChatBotProfileQuery(userId);
  const [createProfile, { isLoading: isCreating }] = useCreateAdminChatBotProfileMutation();
  const [updateProfile, { isLoading: isUpdating }] = useUpdateAdminChatBotProfileMutation();

  const [chatId, setChatId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [showApiKey, setShowApiKey] = useState(false);
  const [syncedFromProfile, setSyncedFromProfile] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<"create" | "update" | null>(null);

  const noProfile = isError && (error as { status?: number })?.status === 404;
  const hasProfile = !isError && !!profile;
  const canSeeApiKey = !isCalibrateManager;

  useEffect(() => {
    setSyncedFromProfile(false);
  }, [userId]);

  useEffect(() => {
    if (chatbotDraft !== undefined && chatbotDraft !== null) {
      setChatId(chatbotDraft.chat_id ?? "");
      setApiKey(chatbotDraft.api_key ?? "");
      setSyncedFromProfile(true);
    } else if (hasProfile && profile) {
      setChatId(profile.chat_id ?? "");
      setApiKey(profile.api_key ?? "");
      setSyncedFromProfile(true);
    } else {
      setSyncedFromProfile(false);
    }
  }, [userId, hasProfile, profile?.chat_id, profile?.api_key, chatbotDraft]);

  const handleCreate = async () => {
    try {
      await createProfile({ userId, chat_id: chatId || "pending", api_key: apiKey }).unwrap();
      onSuccess?.("AI Agent profile created.", "success");
      onSaveComplete?.();
    } catch {
      onSuccess?.("Failed to create AI Agent profile.", "error");
    }
  };

  const handleUpdate = async () => {
    try {
      setConfirmOpen(false);
      setConfirmAction(null);
      const payload: { userId: number; chat_id?: string; api_key?: string } = {
        userId,
        chat_id: chatId || profile?.chat_id,
      };
      if (canSeeApiKey) payload.api_key = apiKey;
      await updateProfile(payload).unwrap();
      onSuccess?.("AI Agent config updated.", "success");
      onSaveComplete?.();
    } catch {
      onSuccess?.("Failed to update AI Agent config.", "error");
    }
  };

  const runConfirmAction = () => {
    if (confirmAction === "create") {
      setConfirmOpen(false);
      setConfirmAction(null);
      handleCreate();
    } else if (confirmAction === "update") {
      handleUpdate();
    }
  };

  const getAiAgentConfirmItems = (): string[] => {
    if (confirmAction === "create") {
      const items: string[] = [];
      if (chatId) items.push("Chat ID");
      if (apiKey && canSeeApiKey) items.push("API Key");
      if (items.length === 0) items.push("Chat ID", "API Key");
      return items;
    }
    if (confirmAction === "update" && profile) {
      const items: string[] = [];
      if ((chatId || profile.chat_id) !== profile.chat_id) items.push("Chat ID");
      if (canSeeApiKey && apiKey !== (profile.api_key ?? "")) items.push("API Key");
      return items;
    }
    return [];
  };

  const handleChatIdChange = (value: string) => {
    setChatId(value);
    onChatbotDraftChange?.(
      { chat_id: value, api_key: apiKey },
      profile ? { chat_id: profile.chat_id, api_key: profile.api_key } : null,
    );
  };
  const handleApiKeyChange = (value: string) => {
    setApiKey(value);
    onChatbotDraftChange?.(
      { chat_id: chatId, api_key: value },
      profile ? { chat_id: profile.chat_id, api_key: profile.api_key } : null,
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ px: 2.5, pt: 2, pb: 2 }}>
        <Skeleton variant="text" width="60%" height={32} sx={{ mb: 1.5 }} />
        <Skeleton variant="rounded" height={40} sx={{ mb: 1.5 }} />
        <Skeleton variant="rounded" height={40} />
      </Box>
    );
  }

  const hasChanges =
    syncedFromProfile &&
    ((chatId || profile?.chat_id) !== profile?.chat_id ||
      (canSeeApiKey && apiKey !== (profile?.api_key ?? "")));

  return (
    <Box sx={{ px: 2, pt: 2, pb: 2, display: "flex", flexDirection: "column", flex: 1 }}>
      {(noProfile || hasProfile) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            mb: 2,
            color: hasProfile ? "success.main" : "text.secondary",
            fontSize: "0.875rem",
          }}
        >
          {hasProfile ? (
            <>
              <CheckCircle sx={{ fontSize: 20 }} />
              <Typography variant="body2" color="success.main" fontWeight={600}>
                Profile saved — you can edit the config below
              </Typography>
            </>
          ) : (
            <>
              <AddCircleOutline sx={{ fontSize: 20 }} />
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                First-time setup — no AI Agent profile yet
              </Typography>
            </>
          )}
        </Box>
      )}

      <Alert icon={<InfoOutlined />} severity="info" sx={{ mb: 2, "& .MuiAlert-message": { width: "100%" } }}>
        {API_KEY_HINT}
      </Alert>

      {noProfile && (
        <>
          <TextField
            label="Chat ID"
            value={chatId}
            onChange={(e) => canEdit && handleChatIdChange(e.target.value)}
            placeholder="pending"
            size="small"
            fullWidth
            disabled={!canEdit}
            sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          />
          {canSeeApiKey && (
            <TextField
              label="API Key"
              type={showApiKey ? "text" : "password"}
              value={apiKey}
              onChange={(e) => canEdit && handleApiKeyChange(e.target.value)}
              size="small"
              fullWidth
              disabled={!canEdit}
              sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowApiKey((v) => !v)} edge="end">
                      {showApiKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
          <Box sx={{ mt: "auto" }}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => { setConfirmAction("create"); setConfirmOpen(true); }}
              disabled={!canEdit || isCreating}
              sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, py: 0.9 }}
            >
              {isCreating ? "Creating…" : "Add AI Agent Profile"}
            </Button>
          </Box>
        </>
      )}

      {hasProfile && profile && (
        <>
          <TextField
            label="Chat ID"
            value={chatId || profile.chat_id}
            onChange={(e) => canEdit && handleChatIdChange(e.target.value)}
            size="small"
            fullWidth
            disabled={!canEdit}
            sx={{ mb: 1.5, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
          />
          {canSeeApiKey && (
            <TextField
              label="API Key"
              type={showApiKey ? "text" : "password"}
              value={apiKey}
              onChange={(e) => canEdit && handleApiKeyChange(e.target.value)}
              placeholder="Enter API key"
              size="small"
              fullWidth
              disabled={!canEdit}
              sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowApiKey((v) => !v)} edge="end">
                      {showApiKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          )}
          {isCalibrateManager && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
              API Key is hidden for Calibrate Managers.
            </Typography>
          )}
          <Box sx={{ mt: "auto" }}>
            {hasChanges && (
              <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 1, color: "#ad1457" }}>
                Unsaved changes
              </Typography>
            )}
            <Tooltip title={!hasChanges ? "No changes to save" : ""}>
              <span>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => { setConfirmAction("update"); setConfirmOpen(true); }}
                  disabled={!canEdit || isUpdating || !hasChanges}
                  sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5, py: 0.9 }}
                >
                  {isUpdating ? "Saving…" : "Save Changes"}
                </Button>
              </span>
            </Tooltip>
          </Box>
        </>
      )}

      <Dialog open={confirmOpen} onClose={() => { setConfirmOpen(false); setConfirmAction(null); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Confirm Access Changes</DialogTitle>
        <DialogContent>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={700}
            textTransform="uppercase"
            letterSpacing={0.6}
            display="block"
            sx={{ mb: 0.5 }}
          >
            AI AGENT
          </Typography>
          {getAiAgentConfirmItems().map((item) => (
            <Typography key={item} variant="body2" component="div">
              {item}
            </Typography>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setConfirmOpen(false); setConfirmAction(null); }} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" onClick={runConfirmAction} sx={{ textTransform: "none", fontWeight: 700 }}>Confirm</Button>
        </DialogActions>
      </Dialog>

      {!noProfile && !hasProfile && !isLoading && (
        <Typography variant="body2" color="text.secondary">
          Unable to load AI Agent profile. Please try again.
        </Typography>
      )}
    </Box>
  );
}
